import type { Car } from "../physics/car";
import type { InputState } from "../input/input";
import particleVertSrc from "./shaders/particle.vert";
import particleFragSrc from "./shaders/particle.frag";

interface Particle {
  x: number; y: number; z: number;
  vx: number; vy: number; vz: number;
  life: number;     // 0 = dead, 1 = just born
  maxLife: number;   // seconds
  size: number;
}

export interface ParticleSystem {
  particles: Particle[];
  gl: WebGL2RenderingContext;
  program: WebGLProgram;
  uView: WebGLUniformLocation;
  uProjection: WebGLUniformLocation;
  vao: WebGLVertexArrayObject;
  vbo: WebGLBuffer;
  maxParticles: number;
}

const MAX_PARTICLES = 200;
// Stride: x,y,z, size, alpha = 5 floats per particle
const FLOATS_PER_PARTICLE = 5;

export function createParticleSystem(gl: WebGL2RenderingContext): ParticleSystem {
  const program = createParticleProgram(gl);

  const vao = gl.createVertexArray()!;
  gl.bindVertexArray(vao);

  const vbo = gl.createBuffer()!;
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, MAX_PARTICLES * FLOATS_PER_PARTICLE * 4, gl.DYNAMIC_DRAW);

  const aPos = gl.getAttribLocation(program, "aPos");
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 3, gl.FLOAT, false, 20, 0);

  const aSize = gl.getAttribLocation(program, "aSize");
  gl.enableVertexAttribArray(aSize);
  gl.vertexAttribPointer(aSize, 1, gl.FLOAT, false, 20, 12);

  const aAlpha = gl.getAttribLocation(program, "aAlpha");
  gl.enableVertexAttribArray(aAlpha);
  gl.vertexAttribPointer(aAlpha, 1, gl.FLOAT, false, 20, 16);

  gl.bindVertexArray(null);

  return {
    particles: [],
    gl,
    program,
    uView: gl.getUniformLocation(program, "uView")!,
    uProjection: gl.getUniformLocation(program, "uProjection")!,
    vao,
    vbo,
    maxParticles: MAX_PARTICLES,
  };
}

/** Emit smoke particles based on car state. Call once per frame. */
export function emitSmoke(ps: ParticleSystem, car: Car, input: InputState, dt: number): void {
  const cosA = Math.cos(car.angle);
  const sinA = Math.sin(car.angle);
  const speed = Math.sqrt(car.vx * car.vx + car.vy * car.vy);
  const speedKmh = speed * 3.6;

  // Lateral velocity (sliding)
  const lateralV = Math.abs(-car.vx * sinA + car.vy * cosA);

  // Emission conditions
  const braking = input.brake > 0.7 && speedKmh > 50;
  const sliding = lateralV > 4.0 && speedKmh > 30;

  if (!braking && !sliding) return;

  // Emission rate scales with intensity
  const intensity = braking ? input.brake * (speedKmh / 300) : Math.min(lateralV / 10, 1);
  const emitCount = Math.ceil(intensity * 8 * dt * 60); // ~8 particles per frame at peak

  // Tire positions (world space, physics Y → 3D -Z)
  const wb = car.wheelbase / 2;
  const tirePositions = sliding
    ? [ // Rear tires when sliding
        { x: car.x - cosA * wb, z: -(car.y - sinA * wb) },
        { x: car.x - cosA * wb, z: -(car.y - sinA * wb) },
      ]
    : [ // Front tires when braking
        { x: car.x + cosA * wb, z: -(car.y + sinA * wb) },
        { x: car.x + cosA * wb, z: -(car.y + sinA * wb) },
      ];

  for (let i = 0; i < emitCount && ps.particles.length < ps.maxParticles; i++) {
    const tire = tirePositions[i % tirePositions.length];
    const spread = 0.8;
    const life = 0.6 + Math.random() * 0.6; // 0.6-1.2 seconds

    ps.particles.push({
      x: tire.x + (Math.random() - 0.5) * spread,
      y: car.z + 0.15 + Math.random() * 0.1, // just above terrain
      z: tire.z + (Math.random() - 0.5) * spread,
      // Drift upward and slightly backward
      vx: (Math.random() - 0.5) * 1.5 - car.vx * 0.05,
      vy: 0.8 + Math.random() * 1.2,
      vz: (Math.random() - 0.5) * 1.5 + car.vy * 0.05,
      life: 1.0,
      maxLife: life,
      size: 0.3 + Math.random() * 0.2,
    });
  }
}

/** Update particle positions and remove dead ones. */
export function updateParticles(ps: ParticleSystem, dt: number): void {
  for (let i = ps.particles.length - 1; i >= 0; i--) {
    const p = ps.particles[i];
    p.life -= dt / p.maxLife;
    if (p.life <= 0) {
      ps.particles.splice(i, 1);
      continue;
    }
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    p.z += p.vz * dt;
    // Slow down horizontal drift
    p.vx *= 0.98;
    p.vz *= 0.98;
    // Grow as it ages
    p.size += dt * 1.2;
  }
}

/** Render particles. Call after scene rendering. */
export function renderParticles(
  ps: ParticleSystem,
  viewMatrix: Float32Array,
  projMatrix: Float32Array
): void {
  if (ps.particles.length === 0) return;

  const { gl } = ps;

  // Build vertex data
  const data = new Float32Array(ps.particles.length * FLOATS_PER_PARTICLE);
  for (let i = 0; i < ps.particles.length; i++) {
    const p = ps.particles[i];
    const off = i * FLOATS_PER_PARTICLE;
    data[off] = p.x;
    data[off + 1] = p.y;
    data[off + 2] = p.z;
    data[off + 3] = p.size;
    data[off + 4] = p.life * 0.4; // alpha: 0.4 at birth → 0 at death
  }

  // Upload
  gl.bindBuffer(gl.ARRAY_BUFFER, ps.vbo);
  gl.bufferSubData(gl.ARRAY_BUFFER, 0, data);

  // Render
  gl.useProgram(ps.program);
  gl.uniformMatrix4fv(ps.uView, false, viewMatrix);
  gl.uniformMatrix4fv(ps.uProjection, false, projMatrix);

  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
  gl.depthMask(false); // Don't write to depth (particles are transparent)

  gl.bindVertexArray(ps.vao);
  gl.drawArrays(gl.POINTS, 0, ps.particles.length);
  gl.bindVertexArray(null);

  gl.depthMask(true);
  gl.disable(gl.BLEND);
}

// --- Shader helpers ---

function createShader(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type)!;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const info = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`Particle shader compile error: ${info}`);
  }
  return shader;
}

function createParticleProgram(gl: WebGL2RenderingContext): WebGLProgram {
  const vert = createShader(gl, gl.VERTEX_SHADER, particleVertSrc);
  const frag = createShader(gl, gl.FRAGMENT_SHADER, particleFragSrc);
  const prog = gl.createProgram()!;
  gl.attachShader(prog, vert);
  gl.attachShader(prog, frag);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    throw new Error(`Particle program link error: ${gl.getProgramInfoLog(prog)}`);
  }
  return prog;
}
